/**
 * RTWO Indexer Engine — Event Publisher Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexerEvent, IndexerEventType } from '../domain-models';
import type { IIndexerEventPublisherService, IndexerEventHandler } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { EventBusAdapterRegistry } from '../registry/adapter-registries';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

let eventCounter = 0;

function generateEventId(): string {
  eventCounter += 1;
  return `idxr-evt-${eventCounter}`;
}

export class IndexerEventPublisherService
  extends BaseIndexerService
  implements IIndexerEventPublisherService
{
  readonly serviceId = INDEXER_SERVICE_IDS.EVENT_PUBLISHER;
  readonly serviceName = 'Indexer Event Publisher';
  readonly description = 'Publishes indexer lifecycle events to subscribed consumers.';
  readonly integrations = ['Core Engine', 'System Registry', 'Explorer Platform (future)'];
  readonly requiresRPCSource = false;
  readonly requiresIndexStorage = false;

  private readonly handlers = new Map<IndexerEventType, IndexerEventHandler[]>();

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly eventBusRegistry: EventBusAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'structure_ready';
  }

  publish(eventType: IndexerEventType, payload: Record<string, unknown>): void {
    if (!this.eventBusRegistry.isConfigured()) {
      return;
    }

    const event: IndexerEvent = {
      eventId: generateEventId(),
      eventType,
      moduleId: 'RTWO-IDXR-001',
      timestamp: null,
      payload,
    };

    this.repositories.eventHistory.add(event);

    const handlers = this.handlers.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[IndexerEventPublisher] Handler error for ${eventType}:`, error);
      }
    }
  }

  subscribe(eventType: IndexerEventType, handler: IndexerEventHandler): () => void {
    const existing = this.handlers.get(eventType) ?? [];
    this.handlers.set(eventType, [...existing, handler]);

    return () => {
      const current = this.handlers.get(eventType) ?? [];
      this.handlers.set(
        eventType,
        current.filter((entry) => entry !== handler)
      );
    };
  }

  getRecentEvents(_limit = 20): IndexerEvent[] {
    if (!this.eventBusRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.eventHistory.findRecent(_limit);
  }
}
